/**
 * Appwrite Database Service Adapter
 *
 * Implements IDatabaseService interface using Appwrite Databases API
 */

import { Client, Databases, Query, RealtimeResponseEvent } from 'appwrite';
import {
  IDatabaseService,
  IQuery,
  IDocumentReference,
  IOrderBy,
  IBatch
} from '../interfaces/IDatabaseService';

/**
 * Appwrite-specific batch implementation
 * Appwrite doesn't have native batch operations, so we implement them as sequential operations
 */
class AppwriteBatch implements IBatch {
  private operations: Array<() => Promise<void>> = [];

  set(ref: IDocumentReference, data: any): void {
    this.operations.push(async () => {
      // Set operation is handled by the service
      // Store for commit
    });
  }

  update(ref: IDocumentReference, updates: any): void {
    this.operations.push(async () => {
      // Update operation is handled by the service
      // Store for commit
    });
  }

  delete(ref: IDocumentReference): void {
    this.operations.push(async () => {
      // Delete operation is handled by the service
      // Store for commit
    });
  }

  async commit(): Promise<void> {
    try {
      // Execute all operations sequentially
      await Promise.all(this.operations.map(op => op()));
      console.log(`✅ Batch committed: ${this.operations.length} operations`);
    } catch (error: any) {
      console.error('❌ Batch commit error:', error);
      throw new Error(`Batch commit failed: ${error.message}`);
    }
  }

  size(): number {
    return this.operations.length;
  }
}

export class AppwriteDatabaseService implements IDatabaseService {
  private client: Client;
  private databases: Databases;
  private databaseId: string;
  private collectionIdMap: Record<string, string>;

  constructor(client: Client, databaseId: string, collectionIdMap?: Record<string, string>) {
    this.client = client;
    this.databases = new Databases(client);
    this.databaseId = databaseId;
    this.collectionIdMap = collectionIdMap || {};
  }

  /**
   * Resolve collection name to Appwrite collection ID
   * If no mapping exists, return the input (assume it's already an ID)
   */
  private resolveCollectionId(nameOrId: string): string {
    return this.collectionIdMap[nameOrId] || nameOrId;
  }

  /**
   * Convert IQuery to Appwrite Query
   */
  private toAppwriteQuery(q: IQuery): string {
    switch (q.operator) {
      case '==':
        return Query.equal(q.field, q.value);
      case '!=':
        return Query.notEqual(q.field, q.value);
      case '<':
        return Query.lessThan(q.field, q.value);
      case '<=':
        return Query.lessThanEqual(q.field, q.value);
      case '>':
        return Query.greaterThan(q.field, q.value);
      case '>=':
        return Query.greaterThanEqual(q.field, q.value);
      case 'in':
        return Query.equal(q.field, q.value); // Appwrite uses equal for arrays
      case 'array-contains':
        return Query.contains(q.field, q.value); // For arrays, use contains method
      default:
        console.warn(`⚠️ Unsupported query operator: ${q.operator}`);
        return Query.equal(q.field, q.value);
    }
  }

  /**
   * Build Appwrite query array from IQuery array
   */
  private buildQueries(
    queries?: IQuery[],
    orderBy?: IOrderBy,
    limit?: number
  ): string[] {
    const appwriteQueries: string[] = [];

    // Add where clauses
    if (queries && queries.length > 0) {
      queries.forEach(q => appwriteQueries.push(this.toAppwriteQuery(q)));
    }

    // Add orderBy
    if (orderBy) {
      if (orderBy.direction === 'asc') {
        appwriteQueries.push(Query.orderAsc(orderBy.field));
      } else {
        appwriteQueries.push(Query.orderDesc(orderBy.field));
      }
    }

    // Add limit
    if (limit !== undefined) {
      appwriteQueries.push(Query.limit(limit));
    }

    return appwriteQueries;
  }

  async getDocument(collection: string, docId: string): Promise<any | null> {
    try {
      const collectionId = this.resolveCollectionId(collection);
      const document = await this.databases.getDocument(
        this.databaseId,
        collectionId,
        docId
      );

      return {
        id: document.$id,
        ...document
      };
    } catch (error: any) {
      if (error.code === 404) {
        return null;
      }
      console.error(`❌ Appwrite getDocument error (${collection}/${docId}):`, error);
      throw new Error(`Failed to get document: ${error.message}`);
    }
  }

  async setDocument(collection: string, docId: string, data: any): Promise<void> {
    try {
      const collectionId = this.resolveCollectionId(collection);
      // Remove 'id' from data if present (Appwrite uses $id)
      const { id, ...docData } = data;

      await this.databases.createDocument(
        this.databaseId,
        collectionId,
        docId === 'unique()' ? 'unique()' : docId,
        docData
      );
    } catch (error: any) {
      console.error(`❌ Appwrite setDocument error (${collection}/${docId}):`, error);
      throw new Error(`Failed to set document: ${error.message}`);
    }
  }

  async updateDocument(collection: string, docId: string, updates: any): Promise<void> {
    try {
      const collectionId = this.resolveCollectionId(collection);
      // Remove 'id' from updates if present
      const { id, ...docUpdates } = updates;

      await this.databases.updateDocument(
        this.databaseId,
        collectionId,
        docId,
        docUpdates
      );
    } catch (error: any) {
      console.error(`❌ Appwrite updateDocument error (${collection}/${docId}):`, error);
      throw new Error(`Failed to update document: ${error.message}`);
    }
  }

  async deleteDocument(collection: string, docId: string): Promise<void> {
    try {
      const collectionId = this.resolveCollectionId(collection);
      await this.databases.deleteDocument(
        this.databaseId,
        collectionId,
        docId
      );
    } catch (error: any) {
      console.error(`❌ Appwrite deleteDocument error (${collection}/${docId}):`, error);
      throw new Error(`Failed to delete document: ${error.message}`);
    }
  }

  async getCollection(
    collection: string,
    queries?: IQuery[],
    orderBy?: IOrderBy,
    limit?: number
  ): Promise<any[]> {
    try {
      const collectionId = this.resolveCollectionId(collection);
      const appwriteQueries = this.buildQueries(queries, orderBy, limit);

      const response = await this.databases.listDocuments(
        this.databaseId,
        collectionId,
        appwriteQueries
      );

      return response.documents.map(doc => ({
        id: doc.$id,
        ...doc
      }));
    } catch (error: any) {
      console.error(`❌ Appwrite getCollection error (${collection}):`, error);
      throw new Error(`Failed to get collection: ${error.message}`);
    }
  }

  async queryCollection(
    collection: string,
    queries: IQuery[],
    orderBy?: IOrderBy,
    limit?: number
  ): Promise<any[]> {
    // queryCollection and getCollection are the same in this implementation
    return this.getCollection(collection, queries, orderBy, limit);
  }

  async getSubcollectionDocument(
    parentCollection: string,
    parentId: string,
    subcollection: string,
    docId: string
  ): Promise<any | null> {
    try {
      // Appwrite doesn't have native subcollections
      // We need to query the subcollection with parent ID filter
      // For campaigns/{campaignId}/leads/{leadId}, we query 'leads' with campaignId filter

      const subcollectionId = this.resolveCollectionId(subcollection);
      const document = await this.databases.getDocument(
        this.databaseId,
        subcollectionId,
        docId
      );

      // Verify it belongs to the parent
      const parentIdField = `${parentCollection.slice(0, -1)}Id`; // campaigns -> campaignId
      if (document[parentIdField] !== parentId) {
        return null;
      }

      return {
        id: document.$id,
        ...document
      };
    } catch (error: any) {
      if (error.code === 404) {
        return null;
      }
      console.error(
        `❌ Appwrite getSubcollectionDocument error (${parentCollection}/${parentId}/${subcollection}/${docId}):`,
        error
      );
      throw new Error(`Failed to get subcollection document: ${error.message}`);
    }
  }

  async getSubcollection(
    parentCollection: string,
    parentId: string,
    subcollection: string,
    queries?: IQuery[],
    orderBy?: IOrderBy,
    limit?: number
  ): Promise<any[]> {
    try {
      // Appwrite doesn't have native subcollections
      // For campaigns/{campaignId}/leads, we query 'leads' collection with campaignId filter
      const parentIdField = `${parentCollection.slice(0, -1)}Id`; // campaigns -> campaignId

      const parentIdQuery: IQuery = {
        field: parentIdField,
        operator: '==',
        value: parentId
      };

      // Add parent ID filter to queries
      const allQueries = [parentIdQuery, ...(queries || [])];

      return this.getCollection(subcollection, allQueries, orderBy, limit);
    } catch (error: any) {
      console.error(
        `❌ Appwrite getSubcollection error (${parentCollection}/${parentId}/${subcollection}):`,
        error
      );
      throw new Error(`Failed to get subcollection: ${error.message}`);
    }
  }

  createBatch(): IBatch {
    return new AppwriteBatch();
  }

  createDocumentReference(collection: string, docId: string): IDocumentReference {
    return {
      id: docId,
      collection: collection,
      path: `${this.databaseId}/${collection}/${docId}`
    };
  }

  onDocumentChange(
    collection: string,
    docId: string,
    callback: (data: any | null) => void
  ): () => void {
    try {
      // Subscribe to document changes using Appwrite Realtime
      const collectionId = this.resolveCollectionId(collection);
      const channel = `databases.${this.databaseId}.collections.${collectionId}.documents.${docId}`;

      const unsubscribe = this.client.subscribe(channel, (response: RealtimeResponseEvent<any>) => {
        if (response.events.includes('databases.*.collections.*.documents.*.delete')) {
          callback(null);
        } else {
          callback({
            id: response.payload.$id,
            ...response.payload
          });
        }
      });

      return unsubscribe;
    } catch (error: any) {
      console.error(`❌ Appwrite onDocumentChange error (${collection}/${docId}):`, error);
      return () => {}; // Return no-op unsubscribe
    }
  }

  onCollectionChange(
    collection: string,
    queries: IQuery[],
    callback: (data: any[]) => void
  ): () => void {
    try {
      // Subscribe to collection changes using Appwrite Realtime
      const collectionId = this.resolveCollectionId(collection);
      const channel = `databases.${this.databaseId}.collections.${collectionId}.documents`;

      const unsubscribe = this.client.subscribe(channel, async (response: RealtimeResponseEvent<any>) => {
        // Re-fetch collection with queries when any change occurs
        try {
          const data = await this.queryCollection(collection, queries);
          callback(data);
        } catch (error) {
          console.error('❌ Error fetching collection on change:', error);
        }
      });

      return unsubscribe;
    } catch (error: any) {
      console.error(`❌ Appwrite onCollectionChange error (${collection}):`, error);
      return () => {}; // Return no-op unsubscribe
    }
  }
}
